import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import Sidebar from '../components/ui/Sidebar';
import Header from '../components/ui/Header';
import { useSidebar } from '../contexts/SidebarContext';
import { useNavigate, Link } from 'react-router-dom';
import Icon from '../components/AppIcon';
import { buildApiUrl } from '../config/api';

const Billing = () => {
  const { user, loading: authLoading, getAuthHeaders } = useAuth();
  const { isPaid, isSubscribed, status, loading: subscriptionLoading, billingCycle, subscriptionEndDate, nextBillingDate, refresh } = useSubscription();
  const { sidebarExpanded } = useSidebar();
  const navigate = useNavigate();
  const [cancelling, setCancelling] = useState(false);
  const [forceExpiring, setForceExpiring] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Redirect non-paid users
  useEffect(() => {
    if (!authLoading && !subscriptionLoading && !isPaid) {
      navigate('/upgrade', { replace: true });
    }
  }, [authLoading, subscriptionLoading, isPaid, navigate]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Calculate days until renewal
  const daysUntilRenewal = () => {
    if (!nextBillingDate) return null;
    const now = new Date();
    const renewal = new Date(nextBillingDate);
    const days = Math.ceil((renewal - now) / (1000 * 60 * 60 * 24));
    return days;
  };

  // Get plan details
  const planName = isSubscribed ? 'Subscription Plan' : 'Legacy Plan';
  const planType = billingCycle === 'annual' ? 'Annual' : billingCycle === 'monthly' ? 'Monthly' : 'One-time';
  const annualCost = billingCycle === 'annual' ? 999 : billingCycle === 'monthly' ? 99 : 0;
  const accountId = user?.uid ? `RP-${new Date().getFullYear()}-${user.uid.substring(0, 8).toUpperCase()}` : 'N/A';
  const isMockSubscription = status?.isMock === true;
  const isRazorpayEnabled = Boolean(
    import.meta.env.VITE_RAZORPAY_KEY_ID_TEST || import.meta.env.VITE_RAZORPAY_KEY_ID_LIVE
  );
  const isRazorpayTestMode = import.meta.env.VITE_RAZORPAY_TEST_MODE === 'true';

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will continue to have access until the end of your billing period.')) {
      return;
    }

    setCancelling(true);
    setError(null);
    setSuccess(null);

    try {
      if (isRazorpayEnabled) {
        const subscriptionId = status?.razorpaySubscriptionId;
        if (!subscriptionId) {
          setError('Cannot cancel: subscription ID not found. Please contact support.');
          setCancelling(false);
          return;
        }
        const response = await fetch(buildApiUrl('/payments/cancel-subscription'), {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ subscriptionId }),
        });
        if (!response.ok) {
          const errData = await response.json().catch(() => ({}));
          throw new Error(errData.error || 'Failed to cancel subscription');
        }
        const data = await response.json();
        console.log('[Billing] Subscription cancelled:', data);
      } else {
        const response = await fetch(buildApiUrl('/payments/mock-cancel'), {
          method: 'POST',
          headers: getAuthHeaders(),
        });
        if (!response.ok) throw new Error('Failed to cancel subscription');
        await response.json();
      }

      setSuccess('Subscription cancelled successfully. You will have access until ' + formatDate(subscriptionEndDate));
      await refresh();
    } catch (err) {
      console.error('[Billing] Cancel error:', err);
      setError(err?.message || 'Failed to cancel subscription. Please try again or contact support.');
    } finally {
      setCancelling(false);
    }
  };

  const handleTestForceExpire = async () => {
    if (!window.confirm('Force this subscription to expire now? (Test only – you will see free-tier UI after refresh.)')) return;
    setForceExpiring(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(buildApiUrl('/payments/test-force-expire'), {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Failed to force expire');
      }
      setSuccess(data.message || 'Done. Refresh the page to see free-tier UI.');
      await refresh();
    } catch (err) {
      setError(err?.message || 'Could not force expire');
    } finally {
      setForceExpiring(false);
    }
  };

  // Show loading while checking subscription status
  if (authLoading || subscriptionLoading || !isPaid) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const daysLeft = daysUntilRenewal();
  const isExpiringSoon = daysLeft !== null && daysLeft > 0 && daysLeft <= 7;
  const isExpired = status?.subscriptionStatus === 'subscription_expired';
  // Only show "cancelled" state when user has cancelled AND is not currently active (e.g. re-subscribed)
  const isCancelled =
    Boolean(status?.cancelledAt) && status?.subscriptionStatus !== 'subscribed_active';

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      <Header />
      <main 
        className={`
          transition-all duration-300 ease-spring pt-16 pb-20 lg:pb-8
          ${sidebarExpanded ? 'lg:ml-64' : 'lg:ml-16'}
        `}
      >
        <div className="bg-card border-b border-border mb-8">
          <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-4">
            <div className="flex items-center justify-between">
              <Link 
                to="/wealth-dashboard" 
                className="text-primary font-semibold text-base sm:text-lg hover:text-primary/80 flex items-center gap-2"
              >
                <Icon name="ArrowLeft" size={18} />
                Back to Dashboard Home
              </Link>
              <p className="text-sm sm:text-base text-muted-foreground">
                Account ID: {accountId}
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-[900px] mx-auto px-4 sm:px-6">
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-8">
            Billing & Subscription Management
          </h1>

          {/* Dev Mode Notice */}
          {isMockSubscription && !isRazorpayEnabled && (
            <div className="mb-6">
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                <p className="text-sm text-muted-foreground">
                  <Icon name="AlertCircle" size={16} className="inline mr-2 text-amber-500" />
                  <strong>Development Mode:</strong> This is a mock subscription. Real Razorpay integration will be enabled once API keys are configured.
                </p>
              </div>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="mb-6">
              <div className="bg-success/10 border border-success/20 rounded-lg p-4">
                <p className="text-sm text-success">
                  <Icon name="CheckCircle" size={16} className="inline mr-2" />
                  {success}
                </p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6">
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                <p className="text-sm text-destructive">
                  <Icon name="AlertCircle" size={16} className="inline mr-2" />
                  {error}
                </p>
              </div>
            </div>
          )}

          {/* Expiring Soon Warning */}
          {isExpiringSoon && !isExpired && (
            <div className="mb-6">
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                <p className="text-sm text-amber-600 dark:text-amber-400">
                  <Icon name="AlertCircle" size={16} className="inline mr-2" />
                  <strong>Your subscription is expiring in {daysLeft} days.</strong> Renew now to continue enjoying unlimited features.
                </p>
              </div>
            </div>
          )}

          {/* Expired Warning */}
          {isExpired && (
            <div className="mb-6">
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                <p className="text-sm text-destructive">
                  <Icon name="AlertCircle" size={16} className="inline mr-2" />
                  <strong>Your subscription has expired.</strong> Upgrade to continue using all features.
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Left Column - Status Panel */}
            <div className="space-y-6">
              {/* Current Plan Status */}
              <div className="bg-card rounded-xl border border-border p-6 shadow-sm">
                <h2 className="text-xl font-semibold text-primary mb-6 pb-3 border-b border-border">
                  Current Plan Status
                </h2>
                
                <div className="text-center mb-6">
                  <strong className="block text-2xl sm:text-3xl font-bold text-primary mb-3">
                    {planName}
                  </strong>
                  <span className={`inline-block px-3 py-1.5 rounded-md text-sm font-semibold ${
                    isExpired
                      ? 'bg-destructive/10 text-destructive'
                      : isCancelled
                      ? 'bg-muted text-muted-foreground'
                      : isPaid
                      ? 'bg-success/10 text-success'
                      : 'bg-primary/10 text-primary'
                  }`}>
                    {isExpired ? 'Expired' : isCancelled ? 'Cancelled' : 'Active'}
                  </span>
                  {isCancelled && subscriptionEndDate && (
                    <p className="text-sm text-muted-foreground mt-2">
                      Access until {formatDate(subscriptionEndDate)}
                    </p>
                  )}
                </div>

                <div className="space-y-4">
                  <div className="text-xs text-muted-foreground font-medium mb-2">
                    Subscription Details
                  </div>
                  
                  <div className="flex justify-between items-center py-2 border-b border-border/50">
                    <span className="text-sm text-muted-foreground">Billing Cycle:</span>
                    <span className="text-sm font-semibold text-foreground capitalize">{planType}</span>
                  </div>

                  {nextBillingDate && (
                    <div className="flex justify-between items-center py-2 border-b border-border/50">
                      <span className="text-sm text-muted-foreground">Next Billing:</span>
                      <span className="text-sm font-semibold text-success">{formatDate(nextBillingDate)}</span>
                    </div>
                  )}

                  {subscriptionEndDate && (
                    <div className="flex justify-between items-center py-2 border-b border-border/50">
                      <span className="text-sm text-muted-foreground">Access Until:</span>
                      <span className="text-sm font-semibold text-foreground">{formatDate(subscriptionEndDate)}</span>
                    </div>
                  )}

                  {billingCycle && annualCost > 0 && (
                    <div className="flex justify-between items-center py-2 border-b border-border/50">
                      <span className="text-sm text-muted-foreground">
                        {billingCycle === 'monthly' ? 'Monthly Cost:' : 'Annual Cost:'}
                      </span>
                      <span className="text-sm font-semibold text-foreground">₹{annualCost}</span>
                    </div>
                  )}
                </div>
                
                {/* Action Buttons - hide Cancel when already cancelled */}
                {!isExpired && isSubscribed && !isCancelled && (
                  <button
                    onClick={handleCancelSubscription}
                    disabled={cancelling}
                    className="w-full mt-6 py-2.5 px-4 bg-destructive/10 text-destructive rounded-lg font-semibold text-sm hover:bg-destructive/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {cancelling ? 'Cancelling...' : 'Cancel Subscription'}
                  </button>
                )}
                {isCancelled && subscriptionEndDate && (
                  <>
                    <p className="w-full mt-6 py-2.5 px-4 text-center text-sm text-muted-foreground border border-border rounded-lg">
                      Subscription cancelled · Access until {formatDate(subscriptionEndDate)}
                    </p>
                    {isRazorpayTestMode && (
                      <button
                        type="button"
                        onClick={handleTestForceExpire}
                        disabled={forceExpiring}
                        className="w-full mt-3 py-2 px-4 text-muted-foreground border border-dashed border-border rounded-lg text-xs hover:bg-muted/50 disabled:opacity-50"
                      >
                        {forceExpiring ? 'Applying…' : 'Test: Force expire now (simulate access end)'}
                      </button>
                    )}
                  </>
                )}

                {isExpired && (
                  <button
                    onClick={() => navigate('/upgrade')}
                    className="w-full mt-6 py-2.5 px-4 bg-primary text-white rounded-lg font-semibold text-sm hover:bg-primary/90 transition-colors"
                  >
                    Renew Subscription
                  </button>
                )}
              </div>

              {/* Billing Profile */}
              <div className="bg-card rounded-xl border border-border p-6 shadow-sm">
                <h2 className="text-xl font-semibold text-primary mb-6 pb-3 border-b border-border">
                  Billing Profile
                </h2>
                
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-muted-foreground font-medium mb-1">Email</p>
                    <p className="text-sm font-medium text-foreground">{user?.email || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground font-medium mb-1">Name</p>
                    <p className="text-sm font-medium text-foreground">{user?.displayName || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground font-medium mb-1">Customer ID</p>
                    <p className="text-sm font-mono text-foreground">{accountId}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Features & Support (2/3 width) */}
            <div className="lg:col-span-2 space-y-6">
              {/* Active Features */}
              <div className="bg-card rounded-xl border border-border p-6 shadow-sm">
                <h2 className="text-xl font-semibold text-primary mb-6 pb-3 border-b border-border">
                  Your Active Features
                </h2>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { icon: 'Infinity', text: 'Unlimited Projection Years' },
                    { icon: 'TrendingUp', text: 'Full Cash-Flow Analysis' },
                    { icon: 'Target', text: 'Retirement Planning & Simulations' },
                    { icon: 'BarChart3', text: 'Monte Carlo Projections' },
                    { icon: 'PieChart', text: 'Asset Allocation Routing' },
                    { icon: 'Calendar', text: 'Ad-Hoc & Emergency Planning' },
                    { icon: 'CreditCard', text: 'Debt Prepayment Modeling' },
                    { icon: 'Headphones', text: 'Dedicated Support' },
                  ].map((feature, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 rounded-lg bg-success/5 border border-success/10">
                      <Icon name={feature.icon} size={20} className="text-success flex-shrink-0 mt-0.5" />
                      <span className="text-sm font-medium text-foreground">{feature.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Payment History Placeholder */}
              <div className="bg-card rounded-xl border border-border p-6 shadow-sm">
                <h2 className="text-xl font-semibold text-primary mb-6 pb-3 border-b border-border">
                  Payment History
                </h2>
                
                {status?.subscriptionStartDate && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50 border border-border">
                      <div>
                        <p className="text-sm font-semibold text-foreground">
                          {planName} - {planType}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Started: {formatDate(status.subscriptionStartDate)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-foreground">₹{annualCost}</p>
                        <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-success/10 text-success mt-1">
                          Paid
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                
                {!status?.subscriptionStartDate && (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No payment history available
                  </p>
                )}
              </div>

              {/* Support */}
              <div className="bg-card rounded-xl border border-border p-6 shadow-sm">
                <h2 className="text-xl font-semibold text-primary mb-4">
                  Need Help?
                </h2>
                <p className="text-sm text-muted-foreground mb-4">
                  Have questions about your subscription or billing? Our support team is here to help.
                </p>
                <a
                  href="mailto:support@retirementpro.in"
                  className="inline-flex items-center gap-2 text-primary font-semibold text-sm hover:text-primary/80"
                >
                  <Icon name="Mail" size={16} />
                  Contact Support
                </a>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Billing;

